import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged, map, Observable, startWith } from 'rxjs';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import '../../../../../primitive-extensions/date';
import { Metadata } from '../../../../api/api-types/api-response.api';
import { ApiDisabilityKindUpdateBody } from '../../../../api/api-types/disability-type.api';
import { Assignment } from '../../../../api/app-types/assignment';
import { Client } from '../../../../api/app-types/client.app';
import { DisabilityKind, DisabilityType } from '../../../../api/app-types/disability-type.app';
import { DocumentFile, DocumentFileSubjectType } from '../../../../api/app-types/document.app';
import { DISABILITY_TYPE_SERVICE } from '../../../../api/services/disability-type.api';
import { provideUploadApi } from '../../../../api/services/upload.api';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { LvrSearchableSelect } from '../../../../core/components/searchable-select/searchable-select';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { DocumentUploadControl } from '../../../../shared/document-upload/document-upload';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../../shared/document-upload/document-upload-validators';
import { mapDateOrNull, matDateProviders } from '../../../../shared/helpers/date.func';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { InfoTooltip } from '../../../../core/components/info-tooltip/info-tooltip';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
	selector: 'lvr-disability-type-edit',
	imports: [
		AsyncPipe,
		TranslocoPipe,
		DatePipe,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatButtonModule,
		LvrPaddingDirective,
		LvrAutoSaveDirective,
		LvrField,
		LvrAppendAsteriskPipe,
		LvrInputDirective,
		DocumentUploadControl,
		LvrRestoreActions,
		LvrHeading,
		LvrDefaultActions,
		LvrSearchableSelect,
		InfoTooltip,
		MatTooltip,
	],
	providers: [provideUploadApi, matDateProviders],
	templateUrl: './disability-type-edit.html',
	styleUrl: './disability-type-edit.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DisabilityTypeEdit {
	public hasError = hasError;
	private readonly transloco = inject(TranslocoService);
	public readonly subjectType = DocumentFileSubjectType.DISABILITY_KIND;
	public readonly env = inject(ENVIRONMENT);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly api = inject(DISABILITY_TYPE_SERVICE);
	private readonly data = toSignal(this.route.data);
	public readonly kind = computed(() => this.data()?.['type'].data as DisabilityKind);
	private readonly metadata = computed(() => this.data()?.['type'].metadata as Metadata);
	private readonly editableFieldMap: Record<string, string> = {
		remark: 'remark',
		documents: 'documents',
		diagnosis: 'differentiation',
	};

	private readonly editableSet = computed(() => {
		const fields = this.metadata()?.editOnlyFields ?? [];

		return new Set(fields.map((field) => this.editableFieldMap[field.trim().toLowerCase()] ?? field));
	});

	public readonly hasEditable = computed(() => this.editableSet().size > 0);
	private readonly typesResponse = computed(() => this.data()!['types'] as PaginatedResponse<DisabilityType>);
	public readonly types = computed(() => this.typesResponse().entities);
	public readonly draftId = computed(() => FORM_KEYS.UPDATE_DISABILITY_TYPE + '.' + this.kind().id);
	public readonly canEditDisabilityKindsPermission = hasPermission(
		this.session.permissions,
		'Documents.DisabilityKind.Edit',
	);
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly minStatusWeeks = this.env.typeSettings.minStatusWeeks; // 6 weeks
	public readonly maxStatusDate = signal(new Date());
	public readonly isLoading = signal<boolean>(false);
	private readonly assignment = computed(() => this.data()?.['assignment'] as Assignment | undefined);
	private readonly minStatusDateByWeeks = computed(() => {
		const max = this.maxStatusDate();
		const min = new Date(max);
		min.setDate(max.getDate() - this.minStatusWeeks * 7);
		min.setHours(0, 0, 0, 0);
		return min;
	});
	private readonly contactPhaseStartDate = computed(() => {
		const contactStart = this.assignment()?.contactStart;
		if (!contactStart) return undefined;

		const contactStartDate = new Date(contactStart);
		if (Number.isNaN(contactStartDate.getTime())) return undefined;

		contactStartDate.setHours(0, 0, 0, 0);
		return contactStartDate;
	});
	public readonly isStatusDateBoundByContactPhase = computed(() => {
		const contactPhaseStart = this.contactPhaseStartDate();
		if (!contactPhaseStart) return false;

		return contactPhaseStart > this.minStatusDateByWeeks();
	});
	public readonly minStatusDate = computed(() => {
		const minByWeeks = this.minStatusDateByWeeks();
		const contactPhaseStart = this.contactPhaseStartDate();
		return contactPhaseStart && contactPhaseStart > minByWeeks ? contactPhaseStart : minByWeeks;
	});
	public readonly minRecoveredOnYears = this.env.typeSettings.minRecoveredOnYears; // 2 years
	public readonly maxRecoveredOnDate = signal(new Date());
	public readonly minRecoveredOnDate = computed(() => {
		const max = this.maxRecoveredOnDate();
		const min = new Date(max);
		min.setFullYear(max.getFullYear() - this.minRecoveredOnYears);
		min.setHours(0, 0, 0, 0);
		return min;
	});

	private readonly clientResolved = computed<ClientResolved | undefined>(() => {
		return this.data()?.['client'] as ClientResolved | undefined;
	});
	public readonly client = computed<Client>(() => this.clientResolved()?.value as Client);
	public readonly form = this.initForm();
	public readonly remainingDocumentSlots = computed(() => {
		return Math.max(0, this.env.typeSettings.maxFiles - this.kind().documents.length);
	});

	private readonly _selectedType = toSignal(
		this.form.controls.disabilityType!.valueChanges.pipe(
			startWith(this.form.controls.disabilityType!.value),
			distinctUntilChanged(),
		),
	);

	public readonly subTypes = computed(() => {
		const selectedType = this._selectedType();
		if (!selectedType) return [];
		return this.types().find((t) => t.id === selectedType)?.categoryDiagnoses ?? [];
	});
	public readonly subtypeLabel = (subtype: { description: string }) => subtype.description;

	public constructor() {
		// Show validation of controls
		this.form.controls.statusAsOf.markAsTouched();
		this.form.controls.recoveredOn.markAsTouched();

		this.resetDifferentiation();

		// Disable non-editable controls if the Proof status is frozen
		this.disableFormCtrls();
	}

	/**
	 * Determines whether a form field can be edited based on the
	 * 'editOnlyFields' array provided by the metadata.
	 * @param formField The name of the form field.
	 * @returns True if the editable set has no value (not frozen),
	 * or it has a value and has the form field; false otherwise.
	 */
	public canEdit(formField: string): boolean {
		// If there are editable fields defined, check if this one is included
		// Otherwise, default to true (all fields editable)
		return this.hasEditable() ? this.editableSet().has(formField) : true;
	}

	/**
	 * Disables all form controls except those listed in the editable set.
	 * Runs when the editable set is defined and the Proof status is frozen.
	 */
	private disableFormCtrls() {
		Object.keys(this.form.controls).forEach((key) => {
			const editable = this.canEdit(key);
			const control = this.form.get(key);

			if (editable) {
				control?.enable({ emitEvent: false });
			} else {
				control?.disable({ emitEvent: false });
			}
		});
	}

	public hasFileError(control: AbstractControl | null, ...errors: string[]) {
		if (!control) return '';

		const error = errors.find(
			(error) => control.hasError(error) && (control.dirty || !!control.value?.length || control.touched),
		);

		if (!error) return '';
		return error;
	}

	/**
	 * Handles form cancellation, deletes draft and navigates to disability type detail view.
	 */
	public async cancel() {
		this.draftService.deleteDraft('client', this.draftId());
		await this.router.navigate(['/desk/client', this.client().id, 'disabilities', 'type', this.kind().id]);
	}

	/**
	 * Handles disability type update, deletes draft and navigates to disability type detail view.
	 */
	public async update() {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const kind = this.kind();
		const fv = this.form.getRawValue();

		const mergedDocs = this.hasEditable() ? [...(kind.documents ?? []), ...(fv.documents ?? [])] : (fv.documents ?? []);

		const body: ApiDisabilityKindUpdateBody = {
			id: kind.id,
			documentIds: mergedDocs.map((doc) => doc.id),
			remark: this.canEdit('remark') ? (fv.remark ?? '') : (kind.remark ?? ''),
			categoryDisabilityTypeId: this.canEdit('disabilityType')
				? (fv.disabilityType ?? undefined)
				: kind.disabilityType?.id,
			categoryDiagnoseId: this.canEdit('differentiation')
				? (fv.differentiation ?? undefined)
				: kind.categoryDiagnosis?.id,
			statusAsOf: this.canEdit('statusAsOf')
				? (fv.statusAsOf?.getISODateStringWithoutTime() ?? '')
				: kind.statusAsOf?.getISODateStringWithoutTime(),
			disabledSince: this.canEdit('since') ? (fv.since ?? '') : (kind.since ?? ''),
			recoveredOn: this.canEdit('recoveredOn')
				? (fv.recoveredOn?.getISODateStringWithoutTime() ?? '')
				: kind.recoveredOn?.getISODateStringWithoutTime(),
		};

		this.isLoading.set(true);

		try {
			await this.api.update(this.client().id, body);
			await this.cancel();
			this.ntfySrv.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.disability_type_updated')),
			);
		} catch {
			this.ntfySrv.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.disability_type_update_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	}

	/**
	 * Resets differentiation control value on disability type change.
	 */
	private resetDifferentiation() {
		effect(() => {
			const selectedTypeId = this._selectedType();
			const subtypes = this.subTypes();
			const diffCtrl = this.form.controls.differentiation;

			if (!selectedTypeId) diffCtrl.reset(undefined, { emitEvent: false });

			const allowedIds = new Set(subtypes.map((s) => s.id));
			const diffId = diffCtrl.value;
			const stillValid = diffId != undefined && allowedIds.has(diffId);

			if (!stillValid) {
				diffCtrl.reset(undefined, { emitEvent: false });
				diffCtrl.markAsPristine();
				diffCtrl.markAsUntouched();
			}

			diffCtrl.updateValueAndValidity({ emitEvent: false });
		});
	}

	/**
	 * Initialize form with current disability type data.
	 */
	private initForm() {
		const fb = inject(FormBuilder);
		const canEditFiles = this.canEdit('documents');
		const editable = this.hasEditable();

		const initialDocs = canEditFiles && editable ? [] : (this.kind().documents ?? []);
		const maxFilesLimit =
			canEditFiles && editable
				? this.env.typeSettings.maxFiles - this.kind().documents.length
				: this.env.typeSettings.maxFiles;

		return fb.group({
			disabilityType: fb.control<number | undefined>(this.kind().disabilityType?.id, [Validators.required]),
			differentiation: fb.control<number | undefined>(this.kind().categoryDiagnosis?.id, []),
			documents: fb.control<DocumentFile[]>(initialDocs, [
				maxFiles(maxFilesLimit),
				maxFileSize(this.env.fileSettings.maxFileSize),
				allowedFileTypes(this.env.fileSettings.allowedMimeTypes),
			]),
			statusAsOf: fb.control<Date | null | undefined>(mapDateOrNull(this.kind().statusAsOf), [Validators.required]),
			since: fb.control<string | undefined>(this.kind().since, [
				Validators.maxLength(this.env.typeSettings.maxSinceLength),
			]),
			recoveredOn: fb.control<Date | null | undefined>(mapDateOrNull(this.kind().recoveredOn)),
			remark: fb.control<string | undefined>(this.kind().remark, [
				Validators.maxLength(this.env.typeSettings.maxRemarkLength),
			]),
		});
	}

	/**
	 * Checks if the given date string is valid and different from the default DateOnly.MinValue ('0001-01-01'),
	 * which is returned by the backend when no date is provided.
	 * @param input - Date string to validate.
	 * @returns true if the date is valid and not the default min/max value.
	 */
	public isValidDate(input?: Date | string): boolean {
		if (!input) return false;
		const date = new Date(input);
		if (Number.isNaN(date.getTime())) return false;

		const y = date.getUTCFullYear();
		const m = date.getUTCMonth();
		const d = date.getUTCDate();

		// Min (0001-01-01), Max (9999-12-31) y overflow (10000-01-01 or higher)
		const isMin = y === 1 && m === 0 && d === 1;
		const isMax = y === 9999 && m === 11 && d === 31;
		const isOverflow = y >= 10000; // catches 10000-01-01 shown by the date pipe

		return !(isMin || isMax || isOverflow);
	}

	public isLocalOrDevEnvironment(): boolean {
		const url = window.location.hostname;
		return url.includes('localhost') || url.includes('dev.');
	}

	public getInfoTextKey(typeId: number): string {
		if (this.isLocalOrDevEnvironment()) {
			return `client.disability.info_text_differentiation_local_development.${typeId}`;
		}
		return `client.disability.info_text_differentiation.${typeId}`;
	}

	public hasHintText(typeId: number): Observable<boolean> {
		const key = this.getInfoTextKey(typeId);
		return this.transloco.selectTranslate(key).pipe(map((translated) => translated !== key));
	}

	public differentiationInfoAriaLabel(description: string) {
		return `Info zu Ausdifferenzierung ${description}`;
	}
}
